/**
 * Page-local copy and ids for /destinations.
 *
 * Published transport figures stay in the import tax settings the Drive-Home
 * Cost calculator reads (`services/tax`). This file holds page copy and the
 * explicit distinction between those published routes and enquiry-only
 * destinations, so regional coverage never inherits a Mombasa tariff.
 *
 * Customer photos and stories live on the separate delivered-cars archive.
 */

import { type Crumb } from '@/components/molecules';
import { type IconKey } from '@/content/site';
import { type DeliveryMediaFilter } from '@/services/deliveries';

/** Opens WhatsApp with the context of this page (§60 K/L). */
export const WHATSAPP_MESSAGE = 'Hello Kabex Motors, I would like to ask about delivery.';

/** `Breadcrumbs` renders Home itself; the schema has to be told about it. */
export const crumbs: readonly Crumb[] = [{ label: 'Destinations' }];

export const ROUTES_TITLE_ID = 'delivery-routes-title';
export const HANDOVER_TITLE_ID = 'delivery-handover-title';

export const routes = {
  eyebrow: 'Delivery destinations',
  title: 'From Kampala to the region',
  lede: 'Start with the destinations that have published transport estimates. For other border points and regional destinations, ask us for options tailored to your vehicle and order.',
  /** Over the headline figure in the panel. */
  fromLabel: 'Transport from Mombasa, from',
  bandsLabel: 'By vehicle weight',
  /** The tablist's accessible name, since the tabs are place names on their own. */
  tabsLabel: 'Choose a named delivery destination',
  cta: 'Estimate transport costs',
} as const;

/**
 * One line of context and one mark per destination, keyed by the destination
 * exactly as the tax settings name it.
 *
 * Every entry restates what the place name and the price table already say, so
 * none of it is a new claim needing Kabex's sign-off. A destination added
 * upstream renders without an entry — `useDeliveryRoutes` falls back rather
 * than dropping the route or crashing.
 */
export const destinationMeta: Record<string, { icon: IconKey; note: string }> = {
  'Kampala, Uganda': {
    icon: 'route',
    note: 'A common handover point. Clearing, inland transport and registration depend on the package in your order.',
  },
  'Bunagana, DR Congo': {
    icon: 'clearing',
    note: 'An agreed handover point for some orders. Clearing and onward transport depend on the package.',
  },
  'Nimule, South Sudan': {
    icon: 'clearing',
    note: 'An agreed handover point for some orders. Clearing and onward transport depend on the package.',
  },
  'Katuna, Uganda': {
    icon: 'route',
    note: 'Ask Kabex about delivery options for your order and onward destination.',
  },
};

/** Named border point shown for enquiries; it has no published Mombasa tariff. */
export const enquiryOnlyDestinations = [
  'Katuna, Uganda',
] as const;

/** Regional destinations are enquiry-led and never show the Mombasa tariff table. */
export const regionalCoverage = {
  eyebrow: 'Beyond the named routes',
  title: 'Delivery conversations across Africa',
  lede: 'Looking beyond Kampala or the listed border points? Tell us where the vehicle needs to go and we’ll discuss destination-specific options for your order.',
  regions: [
    {
      title: 'East Africa',
      countries: [
        'Uganda',
        'Kenya',
        'Tanzania',
        'Rwanda',
        'Burundi',
        'South Sudan',
        'DR Congo',
        'Ethiopia',
      ],
    },
    {
      title: 'Across Africa',
      countries: [
        'Malawi',
        'Mozambique',
        'South Africa',
        'Zambia',
        'Other African destinations',
      ],
    },
  ],
  action: 'Ask about your destination',
} as const;

export const REGIONAL_WHATSAPP_MESSAGE =
  'Hello Kabex Motors, I would like to discuss delivery options for a vehicle to my destination.';

export const galleryBridge = {
  eyebrow: 'See the handovers',
  title: 'Explore delivered cars',
  lede: 'The destinations page explains routes and enquiries. The delivered cars archive is where you can browse handover photos, clips and customer stories.',
  action: 'View delivered cars',
} as const;

/** Used for a destination the settings know about and this file does not. */
export const destinationFallback = { icon: 'route' as IconKey, note: '' };

export const handover = {
  eyebrow: 'At handover',
  title: 'What happens when your car arrives',
  lede: 'Your written quotation names the destination, delivery arrangements and services Kabex will handle.',
} as const;

/**
 * The three things that happen at the end of the import, in the order they
 * happen. Kept here rather than in shared content because no other page draws
 * this sequence.
 */
export const handoverStages: readonly { title: string; text: string; icon: IconKey }[] = [
  {
    title: 'Package services completed',
    text: 'Kabex completes the clearing, registration or transport services included in your order.',
    icon: 'verified',
  },
  {
    title: 'You are kept informed',
    text: 'Kabex updates you on the stages included in your package and confirms when the vehicle is ready for handover.',
    icon: 'updates',
  },
  {
    title: 'Handed over at the agreed destination',
    text: 'Kabex hands the vehicle over at the agreed destination recorded in your order.',
    icon: 'registration',
  },
];

/* ───────────────────────────── /deliveries/gallery ─────────────────────────────
 *
 * The child page: the archive itself, as photographs and clips. The parent page
 * above describes delivery — where we go, what it costs, what the day looks
 * like — and this one shows it having happened. Two routes, one folder, the way
 * `/import-guide` and `/import-guide/:slug` share theirs.
 *
 * Every line below is furniture: headings, labels and the ids the `Section`s
 * are labelled by. Nothing here states a figure or makes a claim Kabex has not
 * already approved on the parent page — the counts in the intro are counted off
 * the archive at run time, so they cannot be wrong, and the only words about a
 * particular car come from the archive itself.
 */

/** Opens WhatsApp with the context of the gallery rather than of delivery. */
export const GALLERY_WHATSAPP_MESSAGE =
  'Hello Kabex Motors, I have seen your delivered cars and I would like one like that.';

export const galleryCrumbs: readonly Crumb[] = [
  { label: 'Destinations', to: '/destinations' },
  { label: 'Delivered cars' },
];

export const gallery = {
  eyebrow: 'Customer handovers',
  title: 'Delivered cars, in their new homes',
  lede: 'Browse handover photos, clips and customer stories. This archive is here to show the people and vehicles behind the journey; destination options and pricing live on the destinations page.',
  /** Over the grid, beside the filters. */
  resultsLabel: 'Showing',
  loadMore: 'Load more',
  /** The chip row's accessible name, since the chips are place names on their own. */
  placeLabel: 'Filter by destination',
  showLabel: 'Filter by what to show',
  allPlaces: 'Everywhere',
  empty: {
    title: 'Nothing here yet',
    text: 'No handovers match that combination. Clear the filters to see the whole archive.',
    action: 'Show everything',
  },
  /** The one call to action carried inside both viewers. */
  viewerCta: { label: 'Get my quotation', to: '/quote' },
} as const;

/** What the four `show` chips are called, in the order they are drawn. */
export const galleryMediaLabels: Record<DeliveryMediaFilter, string> = {
  all: 'Everything',
  photos: 'Photos',
  videos: 'Videos',
  testimonies: 'Customer stories',
};
